<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(
  () => $stores.appData?.state?.data?.paymentOtpUssdCode || {}
);

// Get country codes from source data keys
const countryCodes = computed(() =>
  Object.keys(sourceData.value).filter((code) => sourceData.value[code])
);

const form = reactive({});

// Initialize form for each country
watch(
  sourceData,
  (data) => {
    if (!data) return;
    Object.keys(data).forEach((code) => {
      if (data[code] && !form[code]) {
        form[code] = {
          text: '',
          ussdCode: ''
        };
      }
      if (data[code] && form[code]) {
        Object.assign(form[code], {
          text: data[code].text || '',
          ussdCode: data[code].ussdCode || ''
        });
      }
    });
  },
  { immediate: true, deep: true }
);

const hasChanges = computed(() => {
  return countryCodes.value.some(
    (code) =>
      form[code]?.text !== sourceData.value?.[code]?.text ||
      form[code]?.ussdCode !== sourceData.value?.[code]?.ussdCode
  );
});

const countryHasChanges = (code) =>
  form[code]?.text !== sourceData.value?.[code]?.text ||
  form[code]?.ussdCode !== sourceData.value?.[code]?.ussdCode;

function save() {
  if (!hasChanges.value) return;

  const payload = { paymentOtpUssdCode: {} };

  countryCodes.value.forEach((code) => {
    payload.paymentOtpUssdCode[code] = {};
    if (form[code].text !== sourceData.value?.[code]?.text) {
      payload.paymentOtpUssdCode[code].text = form[code].text;
    }
    if (form[code].ussdCode !== sourceData.value?.[code]?.ussdCode) {
      payload.paymentOtpUssdCode[code].ussdCode = form[code].ussdCode;
    }
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_USSD, payload);
}

function reset() {
  countryCodes.value.forEach((code) => {
    if (sourceData.value?.[code] && form[code]) {
      Object.assign(form[code], sourceData.value[code]);
    }
  });
}

function resetCountry(code) {
  if (sourceData.value?.[code] && form[code]) {
    Object.assign(form[code], sourceData.value[code]);
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h2 class="text-xl font-semibold">Codes USSD pour OTP paiement</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="reset"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRIES GRID -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="code in countryCodes"
        :key="code"
        class="bg-slate-100 rounded-2xl p-5 shadow"
      >
        <div
          class="flex justify-between items-center mb-4 pb-3 border-b border-gray-300"
        >
          <div>
            <h3 class="font-semibold">
              {{ Countries.find(code)?.countryName || code }}
            </h3>
            <span class="text-sm text-gray-500">{{ code }}</span>
          </div>
          <button
            v-if="countryHasChanges(code)"
            class="text-xs px-3 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
            @click="resetCountry(code)"
          >
            Annuler
          </button>
        </div>

        <div class="space-y-3">
          <div>
            <label class="block mb-1 text-sm font-medium"
              >Texte explicatif</label
            >
            <textarea
              class="inputComponent w-full h-20 resize-none"
              v-model="form[code].text"
              placeholder="Ex: Composez ce code pour obtenir votre OTP..."
            ></textarea>
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium">Code USSD</label>
            <input
              class="inputComponent"
              v-model="form[code].ussdCode"
              placeholder="Ex: *123#"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
